// TEAMS Website - App Component
// Main application with routing setup

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n.js';
import Layout from './components/Layout/Layout.jsx';
import HomePage from './pages/HomePage.jsx';
import './styles/main.scss';

// Lazy load pages for better performance
const AssociationPage = React.lazy(() => import('./pages/AssociationPage.jsx'));
const ProjectPage = React.lazy(() => import('./pages/ProjectPage.jsx'));
const EventsPage = React.lazy(() => import('./pages/EventsPage.jsx'));
const EventDetailPage = React.lazy(() => import('./pages/EventDetailPage.jsx'));
const PressPage = React.lazy(() => import('./pages/PressPage.jsx'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage.jsx'));
const TermsPage = React.lazy(() => import('./pages/TermsPage.jsx'));

// Loading component
const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="loading-spinner__circle"></div>
    <p>Loading...</p>
  </div>
);

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    console.error('Error details:', {
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h1>Something went wrong</h1>
          <p>We apologize for the inconvenience.</p>
          {this.state.error && (
            <details style={{ marginTop: '20px', textAlign: 'left' }}>
              <summary>Error Details</summary>
              <pre style={{ background: '#000000ff', padding: '10px', marginTop: '10px', fontSize: '12px' }}>
                {this.state.error.toString()}
                {this.state.error.stack}
              </pre>
            </details>
          )}
         <a href="/">Return Home</a>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <Router>
          <Layout>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Home page with snap-scroll sections */}
                <Route path="/" element={<HomePage />} />
                
                {/* Dedicated pages */}
                <Route path="/association" element={<AssociationPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/events/:slug" element={<EventDetailPage />} />
                <Route path="/press" element={<PressPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                
                {/* Dynamic project pages */}
                <Route path="/projects/:slug" element={<ProjectPage />} />
                
                {/* Fallback for 404 */}
                <Route 
                  path="*" 
                  element={
                    <div className="not-found">
                      <h1>Page Not Found</h1>
                      <p>The page you are looking for does not exist.</p>
                      <a href="/">Return Home</a>
                    </div>
                  } 
                />
              </Routes>
            </Suspense>
          </Layout>
        </Router>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
